import classNames from 'classnames';
import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';
import { border, fontSize, fontWeight, spacing } from '../common/theme/constants';

export declare type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'circle'
  | 'link'
  | 'link_no_padding'
  | 'danger';
export declare type ButtonSize = 'sm' | 'lg';

export interface KitButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  // `any` was the only type I could seem to use to make the typscript compiler stop warning about `as`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: any;
  variant?: ButtonVariant;
  size?: ButtonSize;
  active?: boolean;
  disabled?: boolean;
  forceAriaDisabled?: boolean;
  diameter?: number;
  'aria-label'?: string;
  tabIndex?: number;
  // href would only be applicable if the 'as' property value is 'a'
  href?: string;
}

const CIRCLE_DIAMETER = 1.5;

const StyledButton = styled.button<KitButtonProps>`
  color: ${({ theme }) => theme.button.primary.normal.text};
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-size: ${fontSize.p}rem;
  font-weight: ${fontWeight.normal};
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  user-select: none;
  padding: 6px 12px;
  line-height: 1.5;
  overflow: visible;
  text-transform: none;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  min-width: 96px;

  &:focus {
    outline-offset: 2px;
    outline: 2px solid ${({ theme }) => theme.focus};
  }

  &.sm {
    padding: ${spacing.absolute.xs}px ${spacing.absolute.xs}px;
    font-size: ${fontSize.text_14}rem;
    line-height: 1.5;
  }

  &.lg {
    padding: ${spacing.absolute.s}px ${spacing.absolute.m}px;
    font-size: ${fontSize.text_20}rem;
    line-height: 1.5;
  }

  &:not(:disabled):not(.disabled) {
    cursor: pointer;
  }

  &.primary {
    border: 1px solid ${({ theme }) => theme.button.primary.normal.bg};
    background-color: ${({ theme }) => theme.button.primary.normal.bg};
    color: ${({ theme }) => theme.button.primary.text};
  }

  &.primary:focus {
    background-color: ${({ theme }) => theme.button.primary.hover.bg};
  }

  &.primary:hover {
    background-color: ${({ theme }) => theme.button.primary.hover.bg};
  }

  &.primary:active,
  &.primary:not(:disabled):not(.disabled).active,
  &.primary:not(:disabled):not(.disabled):active {
    background-color: ${({ theme }) => theme.button.primary.hover.bg};
  }

  &.primary.disabled,
  &.primary:disabled {
    border: 1px solid ${({ theme }) => theme.button.primary.disabled.bg};
    color: ${({ theme }) => theme.button.primary.disabled.text};
    background-color: ${({ theme }) => theme.button.primary.disabled.bg};
  }

  &.secondary {
    border: 1px solid ${({ theme }) => theme.button.secondary.normal.border};
    background-color: ${({ theme }) => theme.button.secondary.normal.bg};
    color: ${({ theme }) => theme.button.secondary.normal.text};
  }

  &.secondary:hover {
    border: 1px solid ${({ theme }) => theme.button.primary.hover.bg};
    background-color: ${({ theme }) => theme.button.secondary.hover.bg};
  }

  &.secondary:active,
  &.secondary:not(:disabled):not(.disabled):active,
  &.secondary:not(:disabled):not(.disabled).active {
    background-color: ${({ theme }) => theme.button.secondary.hover.bg};
  }

  &.secondary.disabled,
  &.secondary:disabled {
    border: ${border.width}px solid ${({ theme }) => theme.button.secondary.disabled.border};
    color: ${({ theme }) => theme.button.secondary.disabled.text};
    background-color: ${({ theme }) => theme.button.secondary.bg};
    pointer-events: none;
  }

  &.circle {
    background-color: transparent;
    color: ${({ theme }) => theme.button.circle.normal.text};
    font-weight: ${fontWeight.bold};
    width: ${(props) => props.diameter || CIRCLE_DIAMETER}rem;
    min-width: initial;
    height: ${(props) => props.diameter || CIRCLE_DIAMETER}rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    padding: 0;

    &:hover {
      background-color: ${({ theme }) => theme.button.circle.hover.bg};
      box-shadow: none;
      border-color: transparent;
    }

    &:not(:disabled):not(.disabled).active,
    &:not(:disabled):not(.disabled):active {
      background-color: ${({ theme }) => theme.button.circle.disabled.bg};
    }
  }

  &.link {
    background-color: transparent;
    color: ${({ theme }) => theme.button.link.normal.text};
    border: none;

    &:hover {
      background-color: ${({ theme }) => theme.button.link.hover.bg};
    }

    &:focus {
      outline-offset: 2px;
      outline: 2px solid ${({ theme }) => theme.focus};
    }
  }

  &.link_no_padding {
    background-color: transparent;
    padding: 0;
    min-width: auto;
    color: ${({ theme }) => theme.link.text} !important;
    border: none;

    &:hover {
      background-color: ${({ theme }) => theme.button.link.hover.bg};
      font-weight: ${fontWeight.normal};
      text-decoration: underline;
    }

    &:focus {
      outline-offset: 2px;
      outline: 2px solid ${({ theme }) => theme.focus};
    }
  }

  &.danger {
    background-color: ${({ theme }) => theme.button.danger.normal.bg};
    border: 1px solid transparent;
    color: ${({ theme }) => theme.button.danger.normal.text};

    svg {
      fill: ${({ theme }) => theme.button.danger.normal.text};
    }

    &:hover {
      border: 1px solid ${({ theme }) => theme.button.danger.normal.text};
    }
  }

  &[aria-disabled='true'] {
    pointer-events: none;
  }

  &.primary[aria-disabled='true'] {
    border: 1px solid ${({ theme }) => theme.button.primary.disabled.bg};
    color: ${({ theme }) => theme.button.primary.disabled.text};
    background-color: ${({ theme }) => theme.button.primary.disabled.bg};
  }

  &.secondary[aria-disabled='true'] {
    border: ${border.width}px solid ${({ theme }) => theme.button.secondary.disabled.border};
    color: ${({ theme }) => theme.button.secondary.disabled.text};
    background-color: ${({ theme }) => theme.button.secondary.bg};
  }
`;

const KitButton = forwardRef((props: KitButtonProps, ref: React.RefObject<HTMLButtonElement>) => {
  const {
    className,
    variant = 'primary',
    active,
    size,
    tabIndex,
    disabled,
    onClick,
    ...otherProps
  } = props;
  const activeClass = active ? 'active' : '';

  return (
    <StyledButton
      ref={ref}
      tabIndex={tabIndex}
      /*
        Try to use "forceAriaDisabled" most of the time as eventually it'll replace "disabled"
        If disabled={true} then Not Accessible
        If forceAriaDisabled={true} then Accessible
        If both are true, then default to aria-disabled={true} because that means a conscious decision to pass "forceAriaDisabled={true}" was made, meaning it needs to be accessible
        Note: It cannot have "disabled" AND still be accessible. 
      */
      disabled={disabled && !otherProps.forceAriaDisabled}
      aria-disabled={otherProps.forceAriaDisabled}
      className={classNames(variant, activeClass, size, className)}
      onClick={otherProps.forceAriaDisabled ? undefined : onClick}
      {...otherProps}
    />
  );
});

export default KitButton;
